import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { StatisticsMetadata } from '../../types/supplierStatistics';
export const displayTime = (value: string | null): string =>
  value
    ? new Intl.DateTimeFormat('sv-SE', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Stockholm',
      }).format(new Date(value))
    : 'Uppgift saknas';
export default function StatisticsNotice({
  metadata,
  compact = false,
}: {
  metadata: StatisticsMetadata;
  compact?: boolean;
}) {
  const incomplete =
    metadata.imports.length < 4 ||
    metadata.imports.some((i) => i.stale || i.status !== 'success');
  const footnote = (
    <Typography
      variant="caption"
      component="p"
      color="text.secondary"
      sx={{ mt: 1 }}
    >
      ¹ Antal = online beställda enheter + butikens nettorader. ² Butikens
      momsgrund är inte verifierad; online är inkl moms. Jämförelsen är
      preliminär. Saknad historik är inte säkerställd nollförsäljning.
    </Typography>
  );
  return (
    <Box sx={{ mb: 3 }}>
      {incomplete && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Underlag saknas, är inaktuellt eller uppdateras för:{' '}
          {['online', 'retail', 'catalog', 'stock']
            .filter((source) => {
              const entry = metadata.imports.find((i) => i.source === source);
              return !entry || entry.stale || entry.status !== 'success';
            })
            .map(
              (source) =>
                ({
                  online: 'Online',
                  retail: 'Butik',
                  catalog: 'Sortiment',
                  stock: 'Lager',
                })[source],
            )
            .join(', ')}
          . Se uppdateringsstatus nedan.
        </Alert>
      )}
      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">
            Preliminär årsjämförelse · Underlag och uppdateringar
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Försäljning {metadata.period.start} – {metadata.period.end}.
            Jämförelse {metadata.comparisonPeriod.start} –{' '}
            {metadata.comparisonPeriod.end} (preliminär). Aktuellt lager
            redovisas separat.
          </Typography>
          <Box>
            {['online', 'retail', 'catalog', 'stock'].map((source) => {
              const entry = metadata.imports.find((i) => i.source === source);
              return (
                <Typography key={source} variant="caption" component="div">
                  {source === 'online'
                    ? 'Online'
                    : source === 'retail'
                      ? 'Butik'
                      : source === 'catalog'
                        ? 'Sortiment'
                        : 'Lager'}
                  : senaste lyckade uppdatering{' '}
                  {displayTime(entry?.lastSuccess ?? null)}
                  {entry?.status === 'failed'
                    ? ' · Import misslyckades, föregående underlag visas'
                    : entry?.status === 'running'
                      ? ' · Import pågår'
                      : ''}
                  {entry?.stale ? ' · Inaktuellt underlag' : ''}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="caption">
            Lager: senaste observerade uppdatering{' '}
            {metadata.stockAsOf ?? 'uppgift saknas'}. Tomma värden betyder att
            underlag saknas.
          </Typography>
          {metadata.warnings.map((w) => (
            <Typography key={w} variant="body2" sx={{ mb: 1 }}>
              {w}
            </Typography>
          ))}
          {compact && footnote}
        </AccordionDetails>
      </Accordion>
      {!compact && footnote}
    </Box>
  );
}
